import React, { useId, useMemo, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import { FaArrowRight, FaEye, FaEyeSlash, FaSpinner, FaUserShield, FaBolt } from "react-icons/fa";
import _ from "lodash";
import toast from "react-hot-toast";
import { SEEDED_LOGIN_EMAIL_DOMAIN, SeedLoginGroup } from "constants/seed-login-users";
import { loginValidation } from "schemas";
import { LoginValueProps } from "types";
import {
  LoginFormActionButton,
  LoginFormPrimaryButton,
  LoginFormSection,
  LoginFormContentStackQuaternary,
  LoginFormContentStack,
  LoginFormFieldShell,
  LoginFormHeaderRow,
  LoginFormInfoRow,
  LoginFormContentStackSecondary,
  LoginFormContentStackTertiary,
  LoginFormPanelCard,
  LoginFormFormStack,
  LoginFormSectionHeading,
  LoginFormFieldInput,
  LoginFormFieldInputSecondary,
  LoginFormFieldLabel,
  LoginFormLabelText,
  LoginFormBodyTextSecondary,
  LoginFormEyebrowText,
  LoginFormTextBlock,
  LoginFormMetaText,
  LoginFormBodyText,
  LoginFormFieldSelect,
  LoginFormInlineText,
  LoginFormInlineTextSecondary,
} from "components/molecules/forms/login/styles/login-form.inline.styles";
interface LoginFormProps {
  initialValues: LoginValueProps;
  submitForm: (values: LoginValueProps) => Promise<void>;
  onSuccess: () => void;
  quickLoginGroups?: SeedLoginGroup[];
}
export const LoginForm: React.FC<LoginFormProps> = ({
  initialValues,
  submitForm,
  onSuccess,
  quickLoginGroups = [],
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedQuickLoginId, setSelectedQuickLoginId] = useState("");
  const quickLoginSelectId = useId();
  const quickLoginOptions = useMemo(
    () => quickLoginGroups.flatMap((group) => group.options),
    [quickLoginGroups],
  );
  const handleFormSubmitError = (error: any, actions: FormikHelpers<LoginValueProps>) => {
    const apiErrors = _.get(error, "errors");
    if (!_.isEmpty(apiErrors)) {
      actions.setErrors(apiErrors);
      return;
    }
    const message =
      error?.response?.data?.error ||
      error?.message ||
      "The email or password you entered is incorrect.";
    actions.setFieldError("password", message);
    toast.error(message);
  };
  const handleSubmission = (formData: LoginValueProps, actions: FormikHelpers<LoginValueProps>) => {
    submitForm(formData)
      .then(() => onSuccess())
      .catch((error: any) => {
        handleFormSubmitError(error, actions);
      })
      .finally(() => actions.setSubmitting(false));
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginValidation}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmission}
      enableReinitialize
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        errors,
        handleChange,
        handleBlur,
        handleReset,
        setErrors,
        setTouched,
        setValues,
      }) => {
        const selectedQuickLogin =
          quickLoginOptions.find((option) => option.id === selectedQuickLoginId) || null;
        const applyQuickLogin = (nextId: string) => {
          setSelectedQuickLoginId(nextId);
          const option = quickLoginOptions.find((entry) => entry.id === nextId);
          if (!option) {
            return;
          }
          setErrors({});
          setTouched({});
          setValues({
            ...values,
            password: option.password,
            username: option.username,
          });
        };
        return (
          <LoginFormFormStack onReset={handleReset} onSubmit={handleSubmit}>
            <LoginFormContentStack>
              <LoginFormInfoRow>
                <FaUserShield size={12} />
                Portal Sign In
              </LoginFormInfoRow>
              <div>
                <LoginFormSectionHeading>Welcome back to Athspire</LoginFormSectionHeading>
                <LoginFormBodyText>
                  Sign in with your Athspire account to open the right workspace for your role,
                  assignments, and current API access profile.
                </LoginFormBodyText>
              </div>
            </LoginFormContentStack>

            <LoginFormContentStackSecondary>
              {quickLoginGroups.length ? (
                <LoginFormContentStackTertiary>
                  <LoginFormFieldLabel htmlFor={quickLoginSelectId}>
                    Quick login
                  </LoginFormFieldLabel>
                  <LoginFormPanelCard>
                    <LoginFormSection>
                      <LoginFormInlineText>
                        <FaBolt size={14} />
                      </LoginFormInlineText>
                      <LoginFormContentStackQuaternary>
                        <div>
                          <LoginFormLabelText>Use a seeded account</LoginFormLabelText>
                          <LoginFormBodyTextSecondary>
                            Pick a test user to autofill the login form instantly. All listed
                            accounts use{" "}
                            <LoginFormInlineTextSecondary>password123</LoginFormInlineTextSecondary>
                            .
                          </LoginFormBodyTextSecondary>
                        </div>

                        <LoginFormFieldSelect
                          id={quickLoginSelectId}
                          onChange={(event) => applyQuickLogin(event.target.value)}
                          value={selectedQuickLoginId}
                        >
                          <option value="">Select a test user</option>
                          {quickLoginGroups.map((group) => (
                            <optgroup key={group.id} label={group.label}>
                              {group.options.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.label} ({option.username})
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </LoginFormFieldSelect>

                        {selectedQuickLogin ? (
                          <LoginFormEyebrowText>
                            {selectedQuickLogin.description || selectedQuickLogin.label}
                          </LoginFormEyebrowText>
                        ) : null}
                      </LoginFormContentStackQuaternary>
                    </LoginFormSection>
                  </LoginFormPanelCard>
                </LoginFormContentStackTertiary>
              ) : null}

              <LoginFormContentStackTertiary>
                <LoginFormFieldLabel htmlFor="username">Email</LoginFormFieldLabel>
                <LoginFormFieldInput
                  autoComplete="username"
                  id="username"
                  name="username"
                  onBlur={handleBlur("username")}
                  onChange={handleChange("username")}
                  placeholder={`admin@${SEEDED_LOGIN_EMAIL_DOMAIN}`}
                  type="email"
                  value={values.username}
                />
                {errors.username ? (
                  <LoginFormTextBlock>{errors.username.toString()}</LoginFormTextBlock>
                ) : null}
              </LoginFormContentStackTertiary>

              <LoginFormContentStackTertiary>
                <LoginFormFieldLabel htmlFor="password">Password</LoginFormFieldLabel>
                <LoginFormFieldShell>
                  <LoginFormFieldInputSecondary
                    autoComplete="current-password"
                    id="password"
                    name="password"
                    onBlur={handleBlur("password")}
                    onChange={handleChange("password")}
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                  />
                  <LoginFormActionButton
                    onClick={() => setShowPassword((current) => !current)}
                    type="button"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </LoginFormActionButton>
                </LoginFormFieldShell>
                {errors.password ? (
                  <LoginFormTextBlock>{errors.password.toString()}</LoginFormTextBlock>
                ) : null}
              </LoginFormContentStackTertiary>
            </LoginFormContentStackSecondary>

            <LoginFormHeaderRow>
              <LoginFormMetaText>
                Seed example:{" "}
                <LoginFormInlineTextSecondary>
                  {`admin@${SEEDED_LOGIN_EMAIL_DOMAIN}`}
                </LoginFormInlineTextSecondary>
              </LoginFormMetaText>
              <LoginFormPrimaryButton disabled={isSubmitting} type="submit">
                {isSubmitting ? "Signing in..." : "Enter workspace"}
                {isSubmitting ? (
                  <FaSpinner aria-hidden="true" className="animate-spin" size={12} />
                ) : (
                  <FaArrowRight size={12} />
                )}
              </LoginFormPrimaryButton>
            </LoginFormHeaderRow>
          </LoginFormFormStack>
        );
      }}
    </Formik>
  );
};
